/**
 * Utility for direct Zebra printing via Zebra Browser Print.
 * Requires Zebra Browser Print software installed on the client machine.
 */

const BROWSER_PRINT_URL = 'http://localhost:9100/';

interface ZebraDevice {
    uid: string;
    name: string;
    deviceType: string;
    connection: string;
}

export class ZebraPrinter {
    /**
     * Discovers local Zebra printers via Browser Print
     */
    static async getAvailablePrinters(): Promise<ZebraDevice[]> {
        try {
            const response = await fetch(`${BROWSER_PRINT_URL}available`, {
                method: 'GET',
                headers: { 'Content-Type': 'text/plain' },
            });

            if (!response.ok) {
                throw new Error('Browser Print is not responding. Is it installed and running?');
            }

            const data = await response.json();
            return data.printer || [];
        } catch (error) {
            console.error('Error discovering Zebra printers:', error);
            throw new Error('No se pudo conectar con Zebra Browser Print. Asegúrate de que esté instalado y en ejecución.');
        }
    }

    /**
     * Sends ZPL data to the default or specified printer
     */
    static async printZPL(zpl: string, device?: ZebraDevice): Promise<boolean> {
        try {
            const targetDevice = device || (await this.getDefaultPrinter());

            if (!targetDevice) {
                throw new Error('No se encontró ninguna impresora Zebra configurada.');
            }

            const payload = {
                device: {
                    name: targetDevice.name,
                    uid: targetDevice.uid,
                    deviceType: targetDevice.deviceType,
                    connection: targetDevice.connection
                },
                data: zpl
            };

            const response = await fetch(`${BROWSER_PRINT_URL}write`, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain' },
                body: JSON.stringify(payload)
            });

            return response.ok;
        } catch (error) {
            console.error('Error printing ZPL:', error);
            throw error;
        }
    }

    private static async getDefaultPrinter(): Promise<ZebraDevice | null> {
        try {
            const response = await fetch(`${BROWSER_PRINT_URL}default`, {
                method: 'GET',
                headers: { 'Content-Type': 'text/plain' },
            });

            if (!response.ok) return null;

            const data = await response.json();
            return data || null;
        } catch {
            return null;
        }
    }
}
